import { call } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared/enum";
import { Company, Deal, MapEntity, Person } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import {
  PinLegendsFilter,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import { callApi } from "@app/store/api/callApi";
import getPinLegendsFilter from "@app/util/map/getPinLegendsFilter";

export function* findCompanies(orgId: Organization["id"], companyIds?: Company["id"][]) {
  // Use fetchPins (POST) rather than fetchCompanies (GET) so a long
  // companyIds list can't blow past the ~64KB URL limit on large orgs.
  const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.COMPANY]);
  const pinsResponse: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, {
    $filters: {
      entities: {
        accounts: companyIds
          ? { $and: [{ id: { $in: companyIds } }], includeGroups: true }
          : { includeGroups: true },
      },
      includeAccessStatus: true,
      includeCustomFields: true,
      pinLegends,
    },
    $limit: 100,
    $order: "name",
  });
  return pinsResponse.data as Company[];
}

export function* findPeople(orgId: Organization["id"], companyId?: Company["id"]) {
  const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.PERSON]);
  const peopleResponse: ListResponse<Person> = yield callApi("fetchPeople", orgId, {
    $filters: {
      ...(companyId
        ? {
            $and: [
              {
                "accounts.id": { $in: [companyId] },
              },
            ],
          }
        : undefined),
      includeAccessStatus: true,
      includeCustomFields: true,
      includeGroups: true,
      pinLegends,
    },
    $limit: 100,
    $order: "name",
  });
  return peopleResponse.data;
}

export function* findDeals(
  orgId: Organization["id"],
  companyId?: Company["id"],
  personId?: Person["id"]
) {
  const filters: PlatformFilterCondition[] = [];
  if (companyId) {
    filters.push({ accountId: companyId });
  }
  if (personId) {
    filters.push({ contactId: personId });
  }
  const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.DEAL]);
  const dealsResponse: ListResponse<Deal> = yield callApi("fetchDeals", orgId, {
    $filters: {
      ...(filters.length ? { $and: filters } : undefined),
      includeCustomFields: true,
      includeGroups: true,
      pinLegends,
    },
    $limit: 100,
    $order: "name",
  });
  return dealsResponse.data;
}
